<template>
  <div
    :class="[
      'content-link-page-container',
      { 'xs-and-up': isXSmallScreenAndUp, 'sm-and-down': isSmallScreenAndDown },
    ]"
  >
    <div
      :class="[
        'content-link-text-content',
        { 'no-image': !hasImage, 'sm-and-down': isSmallScreenAndDown },
      ]"
    >
      <div
        :class="['image-container', { 'no-image': !hasImage, 'sm-and-down': isSmallScreenAndDown }]"
      >
        <transition name="appear">
          <img
            v-if="hasImage && !imageBroken"
            v-show="imageLoaded"
            :src="imageUrl"
            :alt="imageAltText"
            :class="['content-link-thumbnail', { 'sm-and-down': isSmallScreenAndDown }]"
            @error="handleLoadImageError"
            @load="handleLoaded"
          />
          <BrokenImage v-else-if="imageBroken" />
        </transition>
      </div>
      <div
        :class="[
          'content-link-text-container',
          { 'no-image': !hasImage, 'sm-and-down': isSmallScreenAndDown },
        ]"
      >
        <div ref="textContainer" class="text-container">
          <h2 class="title">{{ title }}</h2>
          <p class="description">{{ description }}</p>
        </div>

        <FriedFloater
          :class="['button-container', { 'xs-and-down': !isXSmallScreenAndUp }]"
          :show="tooltipOpen"
          :placement="Placement.BottomEnd"
          :main-axis-offset="0"
          :cross-axis-offset="8"
        >
          <div v-click-outside="closeTooltip" class="url-tooltip">
            <a
              :href="url"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="`${t('open-link-in-new-tab')} - ${url}`"
              @click="closeTooltip"
            >
              {{ url }}
            </a>
          </div>
          <template #reference>
            <FriedButton
              :class="[
                'open-link-btn',
                { 'themed-button': !!buttonBackgroundColor, 'xs-and-down': !isXSmallScreenAndUp },
              ]"
              data-external="content-link-page-open-link-button"
              @click="handleClick"
            >
              <span class="open-link-btn-text"> {{ buttonText }}</span>
            </FriedButton>
          </template>
        </FriedFloater>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ref, computed, onMounted, onBeforeUnmount, useTemplateRef, nextTick } from 'vue';
import { Placement } from '@getaccept/fried-tofu';
import type { ContentLinkBlock } from '@getaccept/lib-shared/src/types/content-link';
import * as UrlHelper from '@getaccept/lib-shared/src/helpers/url.helper';
import { CssVariables } from '@getaccept/editor-lib';
import type { UiStyling } from '../branding/types/ui-styling';
import type { EntityTheme } from '../branding/types/theme';
import { ThemeConstants } from '../branding/constants/theme.constants';
import { openSansV29 } from '../fonts/font-faces';
import type { Font } from '../fonts/types/font';
import { FontService } from '../fonts/font-service';
import useBreakpoints from '../composables/breakpoints.composable';
import BrokenImage from './BrokenImage.vue';

interface Props {
  contentLinkBlock?: ContentLinkBlock;
  documentEdit?: boolean;
  uiStyling?: UiStyling | null;
  theme?: EntityTheme | null;
}

const props = withDefaults(defineProps<Props>(), {
  contentLinkBlock: () => ({}) as ContentLinkBlock,
  documentEdit: false,
  uiStyling: null,
  theme: null,
});

const emit = defineEmits<{
  click: [id: string];
}>();

const { t } = useI18n();
const { isXSmallScreenAndUp, isSmallScreenAndDown } = useBreakpoints();

const imageBroken = ref(false);
const imageLoaded = ref(false);
const tooltipOpen = ref(false);

const textContainer = useTemplateRef<HTMLDivElement>('textContainer');

const imageUrl = computed((): string => props.contentLinkBlock?.thumbUrl || '');

const hasImage = computed(() => !!imageUrl.value);

const buttonText = computed(() => props.contentLinkBlock?.buttonText || t('open-link'));

const title = computed(() => props.contentLinkBlock?.title || '');

const url = computed(() => props.contentLinkBlock?.url || '');

const description = computed(() => props.contentLinkBlock?.description || '');

const imageAltText = computed((): string => {
  if (!hasImage.value) return '';
  return props.contentLinkBlock?.title || t('image');
});

const showTooltip = () => {
  tooltipOpen.value = true;
};

const closeTooltip = () => {
  tooltipOpen.value = false;
};

const openUrl = (contentLinkUrl: string) => {
  const url = UrlHelper.getHttpsUrl(contentLinkUrl);
  window.open(url, '_blank');
  const linkId = props.contentLinkBlock?.id;
  if (linkId && typeof linkId === 'string') {
    emit('click', linkId);
  }
  closeTooltip();
};

const setCustomEditorFontColors = () => {
  if (!textContainer.value) return;

  textContainer.value?.style.setProperty(
    CssVariables.h2Color,
    props.theme?.h2Color ?? ThemeConstants.defaultH2Color
  );

  textContainer.value?.style.setProperty(
    CssVariables.textColor,
    props.theme?.textColor ?? ThemeConstants.defaultTextColor
  );
};

const handleClick = () => {
  if (props.documentEdit) {
    showTooltip();
  } else {
    openUrl(url.value);
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeTooltip();
  }
};

const handleLoadImageError = () => {
  imageBroken.value = true;
};

const handleLoaded = () => {
  imageLoaded.value = true;
};

const buttonBackgroundColor = computed(() => props.uiStyling?.color);
const buttonHoverColor = computed(() => props.uiStyling?.buttonHoverBackgroundColor);
const buttonTextColor = computed(() => props.uiStyling?.buttonTextColor);

const loadFont = async (font: Font, cssVariable: string) => {
  FontService.loadFont(font);
  await nextTick();
  textContainer.value?.style.setProperty(cssVariable, font.fontFamily);
};

const loadFonts = () => {
  const defaultFont = props.theme?.fontSettings?.defaultFont || openSansV29;
  const headingsFont = props.theme?.fontSettings?.headingsFont || openSansV29;

  loadFont(defaultFont, CssVariables.defaultFont);
  loadFont(headingsFont, CssVariables.headingsFont);
};

onMounted(() => {
  if (props.documentEdit) {
    document.addEventListener('keydown', handleKeydown);
  }
  setCustomEditorFontColors();
  loadFonts();
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped lang="scss">
@import '@getaccept/lib-shared/src/legacy-styles/index';

.content-link-page-container {
  display: flex;
  background: var(--white);
  padding: var(--spacing-150);
  font-size: 14px;
  flex-direction: column;
}

.content-link-page-container.xs-and-up {
  border-radius: var(--radius-medium);
}

.content-link-text-content {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.content-link-text-content.no-image {
  width: 100%;
}

.content-link-text-content.sm-and-down {
  flex-direction: column;
}

.content-link-page-container.sm-and-down {
  flex-direction: column;
  padding: var(--spacing-100);
}

.image-container {
  width: 260px;
}

@supports not (-ms-high-contrast: none) {
  .image-container {
    display: flex;
    flex: 260px;
  }
}

.image-container.sm-and-down {
  flex: auto;
  padding-top: var(--spacing-150);
  width: auto;
}

.image-container.no-image {
  display: none;
}

.content-link-thumbnail {
  align-self: center;
  max-width: 100%;
  max-height: 195px;
  object-fit: scale-down;
  border-radius: var(--radius-medium);
}

.content-link-thumbnail.sm-and-down {
  margin-top: var(--spacing-100);
  margin-left: auto;
  margin-right: auto;
}

.content-link-text-container {
  padding-top: 2.4rem;
  display: flex;
  justify-content: space-between;
  flex-flow: column wrap;
  flex: 2 2 66%;
  width: 66%;
  padding-left: var(--spacing-150);
}

.content-link-text-container.sm-and-down {
  padding-top: 0.8rem;
  flex: auto;
  padding-left: 0;
  width: auto;
}

.content-link-text-container.no-image {
  width: 100%;
  padding-left: 0;
}

.text-container {
  width: 100%;
}

.title {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: normal;
  margin: 0;
  color: var(--editor-h2-color);
  font-family: var(--editor-headings-font-family);
}

.description {
  line-height: 1.6;
  margin: var(--spacing-50) 0 var(--spacing-100) 0;
  white-space: pre-line;
  color: var(--editor-text-color);
  font-family: var(--editor-default-font-family);
}

.button-container {
  align-self: flex-end;
}

.button-container.xs-and-down {
  width: 100%;
}

.open-link-btn {
  width: fit-content;
  align-self: flex-end;
  border: none;
  min-width: 200px;
}

.open-link-btn.xs-and-down {
  width: 100%;
  margin-top: var(--spacing-100);
}

.open-link-btn-text {
  margin: auto;
  font-size: var(--paragraph-medium-font-size);
  font-weight: 400;
  overflow-wrap: anywhere;
}

.url-tooltip {
  background: white;
  box-shadow: var(--shadow-2);
  border-radius: var(--radius-medium);
  height: 36px;
  padding: 0 var(--spacing-100);
  display: flex;
  align-items: center;
  z-index: $z-tooltip;
  max-width: 352px;
}

.url-tooltip a {
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;
  text-decoration: none;
  line-height: initial;
  color: var(--blue-45);
  font-weight: 600;
}

.url-tooltip a:hover {
  text-decoration: underline;
}

.url-tooltip a[data-focus-visible-added] {
  outline-color: -webkit-focus-ring-color auto 1px;
}

.themed-button.button.ga-primary {
  background-color: v-bind(buttonBackgroundColor);
  color: v-bind(buttonTextColor);
}

.themed-button.button.ga-primary:hover {
  background-color: v-bind(buttonHoverColor);
}

.themed-button.button.ga-primary:focus {
  background-color: v-bind(buttonBackgroundColor);
}
</style>
